import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getCustomers, 
  getProducts, 
  getCoBrandings,
  createDispatch, 
  createCustomer,
  getTransportOptions,
  getCustomerAddresses,
  getCustomerMappingsLookup,
  createCustomerProductMapping,
  verifyOperatorCode,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getTodayFormatted } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import { SearchableSelectWithAction } from '../components/ui/searchable-select-with-action';
import { DatePicker } from '../components/ui/date-picker';
import { toast } from 'sonner';
import { 
  Save, Plus, Trash2, Loader2, ArrowLeft, UserPlus, 
  Package, Truck, ShoppingCart, CheckCircle2, X,
  ChevronRight, Scale, Boxes, Hash, KeyRound
} from 'lucide-react';

export default function QuickOrder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [cobrandings, setCobrandings] = useState([]);
  const [transportOptions, setTransportOptions] = useState([]);
  
  // Form state
  const [customerId, setCustomerId] = useState('');
  const [customerData, setCustomerData] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('primary');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  const [dateOrderReceived, setDateOrderReceived] = useState('');
  const [dispatchDate, setDispatchDate] = useState('');
  const [customerDeliveryDate, setCustomerDeliveryDate] = useState('');
  const [transportAgent, setTransportAgent] = useState('');
  const [numPallets, setNumPallets] = useState('');
  const [notes, setNotes] = useState('');
  const [dateAutoLinked, setDateAutoLinked] = useState(true); // auto-fill PO Date <-> Order Received
  
  // Supplier code lookup
  const [supplierCodeLookup, setSupplierCodeLookup] = useState({});
  
  // Supplier code options for dropdown
  const [supplierCodeOptions, setSupplierCodeOptions] = useState([]);
  
  // Order Lines
  const [lines, setLines] = useState([]);
  
  // Quick-add state  
  const [addProductId, setAddProductId] = useState('');
  const [addCobrandingId, setAddCobrandingId] = useState('');
  const [addSupplierCode, setAddSupplierCode] = useState('');
  const [addQty, setAddQty] = useState('');
  const [addCases, setAddCases] = useState('');
  const [addWeight, setAddWeight] = useState('');
  
  // New Customer Dialog
  const [newCustomerDialogOpen, setNewCustomerDialogOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  
  // Operator gate
  const [operatorCode, setOperatorCode] = useState('');
  const [operator, setOperator] = useState(null); // { id, code, name }
  const [verifyingOperator, setVerifyingOperator] = useState(false);
  
  // Step tracking
  const [step, setStep] = useState(0); // 0=Operator, 1=Customer, 2=Items, 3=Review
  
  // Success dialog
  const [successDialog, setSuccessDialog] = useState({ open: false, orderNumber: '' });

  // Date auto-fill: when one date is set, auto-fill the other
  const handlePoDateChange = (val) => {
    setPoDate(val);
    if (dateAutoLinked && !dateOrderReceived) setDateOrderReceived(val);
  };
  const handleOrderReceivedDateChange = (val) => {
    setDateOrderReceived(val);
    if (dateAutoLinked && !poDate) setPoDate(val);
  };
  // Once user manually changes the "other" date, unlink auto-fill
  const handlePoDateManualChange = (val) => {
    setPoDate(val);
    if (dateOrderReceived && val !== dateOrderReceived) setDateAutoLinked(false);
  };
  const handleOrderReceivedManualChange = (val) => {
    setDateOrderReceived(val);
    if (poDate && val !== poDate) setDateAutoLinked(false);
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [customersRes, productsRes, cobrandingsRes, transportRes] = await Promise.allSettled([
        getCustomers(), getProducts(), getCoBrandings(), getTransportOptions(),
      ]);
      setCustomers(customersRes.status === 'fulfilled' ? (customersRes.value.data || []) : []);
      setProducts(productsRes.status === 'fulfilled' ? (productsRes.value.data || []) : []);
      setCobrandings(cobrandingsRes.status === 'fulfilled' ? (cobrandingsRes.value.data || []) : []);
      setTransportOptions(transportRes.status === 'fulfilled' ? (transportRes.value.data || []) : []);
    } catch { toast.error('Failed to load data'); }
  };

  const handleVerifyOperator = async () => {
    if (!operatorCode.trim()) { toast.error('Enter your operator code'); return; }
    setVerifyingOperator(true);
    try {
      const res = await verifyOperatorCode(operatorCode.trim());
      setOperator(res.data);
      toast.success(`Welcome, ${res.data.name}!`);
      setStep(1);
    } catch {
      toast.error('Invalid operator code. Please try again.');
    } finally { setVerifyingOperator(false); }
  };

  const handleCustomerChange = async (value) => {
    setCustomerId(value);
    const customer = customers.find(c => c.id === value);
    setCustomerData(customer);
    setCustomerAddresses([]);
    setSelectedAddressId('primary');
    setSelectedAddress(null);
    setSupplierCodeLookup({});
    setSupplierCodeOptions([]);
    
    if (value) {
      try {
        const [addrRes, mapRes] = await Promise.allSettled([
          getCustomerAddresses(value), getCustomerMappingsLookup(value)
        ]);
        if (addrRes.status === 'fulfilled') {
          const all = addrRes.value.data.all_addresses || [];
          setCustomerAddresses(all);
          const def = all.find(a => a.is_default) || all[0];
          if (def) { setSelectedAddressId(def.id); setSelectedAddress(def); }
        } else if (customer) {
          const pa = {
            id: 'primary', label: 'Primary Address',
            address_line1: customer.delivery_address_line1, address_line2: customer.delivery_address_line2,
            city: customer.delivery_city, county: customer.delivery_county,
            postcode: customer.delivery_postcode, country: customer.delivery_country || 'Ireland',
            contact_name: customer.contact_name, contact_phone: customer.contact_phone,
            is_primary: true
          };
          setCustomerAddresses([pa]); setSelectedAddressId('primary'); setSelectedAddress(pa);
        }
        if (mapRes.status === 'fulfilled') {
          setSupplierCodeLookup(mapRes.value.data.lookup || {});
          // Build supplier code dropdown options
          const mappings = mapRes.value.data.mappings || [];
          const opts = mappings.map(m => {
            const product = products.find(p => p.id === m.internal_product_id);
            const cobranding = cobrandings.find(c => c.id === m.cobranding_id);
            const productName = product?.name || 'Unknown Product';
            const pkgLabel = cobranding ? ` (${cobranding.name})` : '';
            return {
              value: m.supplier_product_code,
              displayValue: m.supplier_product_code,
              label: `${m.supplier_product_code} - ${productName}${pkgLabel}`,
              product_id: m.internal_product_id,
              product_name: productName,
              cobranding_id: m.cobranding_id || '',
              cobranding_name: cobranding?.name || ''
            };
          });
          setSupplierCodeOptions(opts);
        }
      } catch {}
    }
  };

  const handleSupplierCodeSelect = (code) => {
    setAddSupplierCode(code);
    if (!code) return;
    const option = supplierCodeOptions.find(o => o.value === code);
    if (option) {
      setAddProductId(option.product_id);
      setAddCobrandingId(option.cobranding_id || '');
      toast.success(`Loaded: ${option.product_name}${option.cobranding_name ? ` (${option.cobranding_name})` : ''}`);
    }
  };

  const handleAddProduct = () => {
    if (!addProductId) { toast.error('Select a product'); return; }
    if (!addQty || parseInt(addQty) <= 0) { toast.error('Enter quantity'); return; }
    
    const product = products.find(p => p.id === addProductId);
    const cobranding = addCobrandingId ? cobrandings.find(c => c.id === addCobrandingId) : null;
    
    // Find supplier code
    let supplierCode = addSupplierCode || '';
    if (!supplierCode) {
      const comboKey = addCobrandingId ? `${addProductId}:${addCobrandingId}` : null;
      if (comboKey && supplierCodeLookup[comboKey]) {
        supplierCode = supplierCodeLookup[comboKey].supplier_product_code || supplierCodeLookup[comboKey];
      } else if (supplierCodeLookup[addProductId]) {
        supplierCode = supplierCodeLookup[addProductId].supplier_product_code || supplierCodeLookup[addProductId];
      }
    }
    
    const newLine = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2),
      product_id: addProductId,
      product_name: product?.name || '',
      cobranding_id: addCobrandingId || '',
      cobranding_name: cobranding?.name || '',
      quantity: addQty,
      cases: addCases || '',
      estimated_weight: addWeight || '',
      notes: '',
      supplier_product_code: supplierCode,
    };
    
    setLines(prev => [...prev, newLine]);
    setAddProductId('');
    setAddCobrandingId('');
    setAddSupplierCode('');
    setAddQty('');
    setAddCases('');
    setAddWeight('');
    toast.success(`${product?.name} added`);
  };

  const removeLine = (lineId) => {
    setLines(prev => prev.filter(l => l.id !== lineId));
  };

  const updateLineField = (lineId, field, value) => {
    setLines(prev => prev.map(l => l.id === lineId ? { ...l, [field]: value } : l));
  };

  const totals = lines.reduce((acc, l) => ({
    units: acc.units + (parseInt(l.quantity) || 0),
    cases: acc.cases + (parseInt(l.cases) || 0),
    weight: acc.weight + (parseFloat(l.estimated_weight) || 0),
  }), { units: 0, cases: 0, weight: 0 });

  const handleSave = async () => {
    if (!customerId) { toast.error('Select a customer'); setStep(1); return; }
    if (lines.length === 0) { toast.error('Add at least one product'); setStep(2); return; }
    
    setSaving(true);
    try {
      let deliveryAddress = '';
      if (selectedAddress) {
        deliveryAddress = [selectedAddress.address_line1, selectedAddress.address_line2, selectedAddress.city, selectedAddress.county, selectedAddress.postcode, selectedAddress.country].filter(Boolean).join(', ');
      }
      
      const orderData = {
        customer_id: customerId,
        status: 'draft',
        date_order_received: dateOrderReceived || getTodayFormatted(),
        po_date: poDate || null,
        dispatch_date: dispatchDate || null,
        customer_delivery_date: customerDeliveryDate || null,
        po_number: poNumber || null,
        vehicle_info: transportAgent || null,
        num_pallets: numPallets ? parseInt(numPallets) : null,
        notes: notes || null,
        operator_code: operator?.code || null,
        operator_name: operator?.name || null,
        delivery_address_id: selectedAddressId || 'primary',
        delivery_address: deliveryAddress || null,
        delivery_contact_name: selectedAddress?.contact_name || null,
        delivery_contact_phone: selectedAddress?.contact_phone || null,
        delivery_instructions: selectedAddress?.delivery_instructions || null,
        lines: lines.map(l => ({
          product_id: l.product_id,
          product_name: l.product_name,
          cobranding_id: l.cobranding_id || null,
          quantity: parseInt(l.quantity),
          cases: parseInt(l.cases) || null,
          estimated_weight: parseFloat(l.estimated_weight) || null,
          notes: l.notes || null,
          supplier_product_code: l.supplier_product_code || null,
        })),
      };
      
      const response = await createDispatch(orderData);
      setSuccessDialog({ open: true, orderNumber: response.data.dispatch_number });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  const handleNewOrder = () => {
    setSuccessDialog({ open: false, orderNumber: '' });
    setCustomerId(''); setCustomerData(null); setLines([]);
    setPoNumber(''); setPoDate(''); setDateOrderReceived('');
    setDispatchDate(''); setCustomerDeliveryDate('');
    setTransportAgent(''); setNumPallets(''); setNotes('');
    setDateAutoLinked(true);
    setSupplierCodeLookup({}); setSupplierCodeOptions([]);
    setAddSupplierCode('');
    setStep(1); // Go back to customer step (operator already verified)
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) { toast.error('Enter customer name'); return; }
    setCreatingCustomer(true);
    try {
      const response = await createCustomer({ name: newCustomerName.trim() });
      const nc = response.data;
      setCustomers(prev => [...prev, nc]);
      setCustomerId(nc.id);
      setCustomerData(nc);
      setNewCustomerDialogOpen(false);
      setNewCustomerName('');
      toast.success('Customer created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally { setCreatingCustomer(false); }
  };

  // Step indicator
  const StepIndicator = () => (
    <div className="flex items-center gap-1 sm:gap-2" data-testid="step-indicator">
      {[
        { n: 1, label: 'Customer', icon: UserPlus },
        { n: 2, label: 'Products', icon: Package },
        { n: 3, label: 'Review', icon: CheckCircle2 },
      ].map(({ n, label, icon: Icon }, i) => (
        <React.Fragment key={n}>
          {i > 0 && <ChevronRight className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />}
          <button
            onClick={() => {
              if (n === 1 || customerId) setStep(n);
            }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all
              ${step === n
                ? 'bg-primary text-primary-foreground shadow-md'
                : step > n
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                  : 'bg-muted text-muted-foreground'
              }`}
            data-testid={`step-${n}`}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col" data-testid="quick-order-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="btn-back-dashboard" className="h-10 w-10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary" />
                Quick Order
              </h1>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {operator && <span className="font-medium text-primary">{operator.name} ({operator.code})</span>}
                {customerData && <>{operator && <span>|</span>}<span>{customerData.name}</span></>}
              </div>
            </div>
          </div>
          {step > 0 && <StepIndicator />}
          <div className="flex items-center gap-2">
            {lines.length > 0 && (
              <Badge variant="secondary" className="text-sm px-3 py-1 font-mono" data-testid="badge-line-count">
                {lines.length} item{lines.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-4 py-6">
        <div className="max-w-5xl mx-auto">
          
          {/* ===== STEP 0: Operator Code ===== */}
          {step === 0 && (
            <div className="flex items-center justify-center min-h-[60vh]" data-testid="step-0-content">
              <div className="text-center space-y-6 w-full max-w-sm">
                <div className="w-20 h-20 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto">
                  <KeyRound className="w-10 h-10" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Operator Sign-In</h2>
                  <p className="text-muted-foreground text-sm mt-2">Enter your operator code to begin</p>
                </div>
                <div className="space-y-3">
                  <Input
                    value={operatorCode}
                    onChange={e => setOperatorCode(e.target.value.toUpperCase())}
                    placeholder="Enter code (e.g. OP01)"
                    className="h-14 text-center text-2xl font-mono tracking-widest"
                    maxLength={10}
                    autoFocus
                    data-testid="input-operator-code-gate"
                    onKeyDown={e => e.key === 'Enter' && handleVerifyOperator()}
                  />
                  <Button
                    size="lg"
                    onClick={handleVerifyOperator}
                    disabled={verifyingOperator || !operatorCode.trim()}
                    className="w-full h-14 text-base rounded-xl shadow-lg"
                    data-testid="btn-verify-operator"
                  >
                    {verifyingOperator ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verifying...</>
                    ) : (
                      <><ChevronRight className="w-5 h-5 mr-2" /> Continue</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Contact your administrator if you don't have an operator code
                </p>
              </div>
            </div>
          )}
          
          {/* ===== STEP 1: Customer & Dates ===== */}
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300" data-testid="step-1-content">
              <div className="text-center pb-4">
                <h2 className="text-xl font-semibold">Who is this order for?</h2>
                <p className="text-muted-foreground text-sm mt-1">Select a customer and set key dates</p>
              </div>

              {/* Customer Card */}
              <div className="bg-card border rounded-2xl p-6 shadow-sm max-w-2xl mx-auto space-y-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Customer *</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <SearchableSelect
                        options={customers.map(c => ({ value: c.id, label: c.name }))}
                        value={customerId}
                        onValueChange={handleCustomerChange}
                        placeholder="Search customers..."
                        data-testid="pos-select-customer"
                      />
                    </div>
                    <Button variant="outline" size="icon" onClick={() => setNewCustomerDialogOpen(true)} className="h-10 w-10 flex-shrink-0" data-testid="pos-btn-new-customer">
                      <UserPlus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Customer Address Preview */}
                {selectedAddress && (
                  <div className="bg-muted/40 rounded-xl p-4 text-sm space-y-1" data-testid="pos-customer-address">
                    <p className="font-medium">{customerData?.name}</p>
                    {selectedAddress.address_line1 && <p className="text-muted-foreground">{selectedAddress.address_line1}</p>}
                    <p className="text-muted-foreground">
                      {[selectedAddress.city, selectedAddress.county, selectedAddress.postcode].filter(Boolean).join(', ')}
                    </p>
                    {customerAddresses.length > 1 && (
                      <Select value={selectedAddressId} onValueChange={(id) => { setSelectedAddressId(id); setSelectedAddress(customerAddresses.find(a => a.id === id)); }}>
                        <SelectTrigger className="mt-2 h-9" data-testid="pos-select-address"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {customerAddresses.map(a => <SelectItem key={a.id} value={a.id}>{a.label}{a.is_default ? ' (Default)' : ''}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                
                {/* Key Dates - Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">PO Number</Label>
                    <Input value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="PO reference" className="h-11 text-base" data-testid="pos-input-po" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">PO Date</Label>
                    <DatePicker value={poDate} onChange={handlePoDateManualChange} placeholder="PO date" data-testid="pos-input-po-date" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Date Order Received</Label>
                    <DatePicker value={dateOrderReceived} onChange={handleOrderReceivedManualChange} placeholder="Order received" data-testid="pos-input-order-received" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className={`text-xs ${!dispatchDate ? 'text-amber-700 font-semibold' : 'text-muted-foreground'}`}>Dispatch Date <span className="text-red-500">*</span></Label>
                    <DatePicker value={dispatchDate} onChange={setDispatchDate} placeholder="Select date" data-testid="pos-input-dispatch-date"
                      className={!dispatchDate ? "border-amber-400 bg-amber-50/60 ring-1 ring-amber-300" : ""} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Delivery Date</Label>
                    <DatePicker value={customerDeliveryDate} onChange={setCustomerDeliveryDate} placeholder="Customer req." data-testid="pos-input-delivery-date" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1"><Truck className="w-3 h-3" /> Transport</Label>
                    <Select value={transportAgent} onValueChange={setTransportAgent}>
                      <SelectTrigger className="h-11" data-testid="pos-select-transport"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {transportOptions.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Pallets</Label>
                    <Input type="number" min="0" value={numPallets} onChange={e => setNumPallets(e.target.value)} placeholder="0" className="h-11 text-base" inputMode="numeric" pattern="[0-9]*" data-testid="pos-input-pallets" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Notes</Label>
                    <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Special instructions..." className="h-11" data-testid="pos-input-notes" />
                  </div>
                </div>
              </div>
              
              {/* Continue */}
              <div className="flex justify-center pt-2">
                <Button
                  size="lg"
                  onClick={() => { if (!customerId) { toast.error('Please select a customer'); return; } setStep(2); }}
                  className="h-14 px-10 text-base rounded-xl shadow-lg"
                  data-testid="pos-btn-continue-to-products"
                >
                  Continue to Products <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* ===== STEP 2: Add Products ===== */}
          {step === 2 && (
            <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300" data-testid="step-2-content">
              
              {/* Quick Add Bar */}
              <div className="bg-card border-2 border-primary/20 rounded-2xl p-5 shadow-sm" data-testid="quick-add-bar">
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Add Product</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 items-end">
                  {supplierCodeOptions.length > 0 && (
                    <div className="space-y-1">
                      <Label className="text-xs">Customer Code</Label>
                      <SearchableSelectWithAction
                        options={supplierCodeOptions}
                        value={addSupplierCode}
                        onValueChange={handleSupplierCodeSelect}
                        placeholder="Code..."
                        searchPlaceholder="Type to search..."
                        emptyMessage="No codes found"
                        data-testid="pos-select-supplier-code"
                      />
                    </div>
                  )}
                  <div className={`${supplierCodeOptions.length > 0 ? '' : 'sm:col-span-2'} space-y-1`}>
                    <Label className="text-xs">Product *</Label>
                    <SearchableSelect
                      options={products.map(p => ({ value: p.id, label: `${p.name}${p.sku ? ` (${p.sku})` : ''}` }))}
                      value={addProductId}
                      onValueChange={setAddProductId}
                      placeholder="Search product..."
                      data-testid="pos-select-product"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Packaging</Label>
                    <Select value={addCobrandingId || 'none'} onValueChange={v => setAddCobrandingId(v === 'none' ? '' : v)}>
                      <SelectTrigger className="h-10" data-testid="pos-select-packaging"><SelectValue placeholder="Standard" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Standard</SelectItem>
                        {cobrandings.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">No. Units *</Label>
                    <Input type="number" min="1" value={addQty} onChange={e => setAddQty(e.target.value)} placeholder="0" className="h-10 text-center font-mono text-lg" inputMode="numeric" pattern="[0-9]*" data-testid="pos-input-qty"
                      onKeyDown={e => e.key === 'Enter' && handleAddProduct()} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Cases</Label>
                    <Input type="number" min="0" value={addCases} onChange={e => setAddCases(e.target.value)} placeholder="0" className="h-10 text-center font-mono" inputMode="numeric" pattern="[0-9]*" data-testid="pos-input-cases"
                      onKeyDown={e => e.key === 'Enter' && handleAddProduct()} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Weight (kg)</Label>
                    <Input type="number" step="0.01" min="0" value={addWeight} onChange={e => setAddWeight(e.target.value)} placeholder="0.00" className="h-10 text-center font-mono" inputMode="decimal" data-testid="pos-input-weight"
                      onKeyDown={e => e.key === 'Enter' && handleAddProduct()} />
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <Button onClick={handleAddProduct} className="h-12 px-8 text-base rounded-xl" data-testid="pos-btn-add-product">
                    <Plus className="w-5 h-5 mr-2" /> Add to Order
                  </Button>
                </div>
              </div>
              
              {/* Order Lines */}
              {lines.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground" data-testid="empty-order">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-lg font-medium">No products added yet</p>
                  <p className="text-sm">Use the form above to add items to this order</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="order-lines-list">
                  {lines.map((line, idx) => (
                    <div key={line.id} className="bg-card border rounded-xl px-4 py-3 flex items-center gap-3 group hover:shadow-sm transition-shadow" data-testid={`pos-line-${idx}`}>
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{line.product_name}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                          {line.supplier_product_code && <span className="bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded font-mono">{line.supplier_product_code}</span>}
                          {line.cobranding_name && <span className="bg-muted px-1.5 py-0.5 rounded">{line.cobranding_name}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm flex-shrink-0">
                        <div className="text-center">
                          <Input type="number" min="1" value={line.quantity} onChange={e => updateLineField(line.id, 'quantity', e.target.value)}
                            className="w-16 h-9 text-center font-mono font-semibold" inputMode="numeric" pattern="[0-9]*" data-testid={`pos-line-qty-${idx}`} />
                          <span className="text-[10px] text-muted-foreground">units</span>
                        </div>
                        <div className="text-center">
                          <Input type="number" min="0" value={line.cases} onChange={e => updateLineField(line.id, 'cases', e.target.value)}
                            className="w-16 h-9 text-center font-mono" inputMode="numeric" pattern="[0-9]*" data-testid={`pos-line-cases-${idx}`} />
                          <span className="text-[10px] text-muted-foreground">cases</span>
                        </div>
                        <div className="text-center">
                          <Input type="number" step="0.01" min="0" value={line.estimated_weight} onChange={e => updateLineField(line.id, 'estimated_weight', e.target.value)}
                            className="w-20 h-9 text-center font-mono" inputMode="decimal" data-testid={`pos-line-weight-${idx}`} />
                          <span className="text-[10px] text-muted-foreground">kg</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeLine(line.id)} className="h-9 w-9 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" data-testid={`pos-btn-remove-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Continue to Review */}
              {lines.length > 0 && (
                <div className="flex justify-center pt-2">
                  <Button size="lg" onClick={() => setStep(3)} className="h-14 px-10 text-base rounded-xl shadow-lg" data-testid="pos-btn-continue-to-review">
                    Review Order <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ===== STEP 3: Review & Submit ===== */}
          {step === 3 && (
            <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300" data-testid="step-3-content">
              <div className="text-center pb-2">
                <h2 className="text-xl font-semibold">Review & Submit</h2>
                <p className="text-muted-foreground text-sm mt-1">Double-check everything before submitting</p>
              </div>
              
              {/* Order Summary Card */}
              <div className="max-w-3xl mx-auto space-y-4">
                {/* Customer Info */}
                <div className="bg-card border rounded-2xl p-5" data-testid="review-customer-info">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Customer</p>
                      <p className="text-lg font-semibold mt-0.5">{customerData?.name}</p>
                      {selectedAddress && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {[selectedAddress.address_line1, selectedAddress.city, selectedAddress.postcode].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="text-right text-sm space-y-1">
                      {operator && <p><span className="text-muted-foreground">Operator:</span> <span className="font-medium text-primary">{operator.name}</span></p>}
                      {poNumber && <p><span className="text-muted-foreground">PO:</span> <span className="font-medium">{poNumber}</span></p>}
                      {poDate && <p><span className="text-muted-foreground">PO Date:</span> <span className="font-medium">{poDate}</span></p>}
                      {dateOrderReceived && <p><span className="text-muted-foreground">Received:</span> <span className="font-medium">{dateOrderReceived}</span></p>}
                      {dispatchDate && <p><span className="text-muted-foreground">Dispatch:</span> <span className="font-medium">{dispatchDate}</span></p>}
                      {transportAgent && <p><span className="text-muted-foreground">Transport:</span> <span className="font-medium">{transportAgent}</span></p>}
                    </div>
                  </div>
                </div>
                
                {/* Line Items */}
                <div className="bg-card border rounded-2xl overflow-hidden" data-testid="review-lines">
                  <div className="px-5 py-3 border-b bg-muted/30">
                    <p className="text-sm font-semibold">{lines.length} Item{lines.length !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="divide-y">
                    {lines.map((line, idx) => (
                      <div key={line.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`review-line-${idx}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{line.product_name}</p>
                            <div className="flex items-center gap-2">
                              {line.supplier_product_code && <span className="text-xs font-mono text-blue-600">{line.supplier_product_code}</span>}
                              {line.cobranding_name && <span className="text-xs text-muted-foreground">{line.cobranding_name}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 font-mono text-right flex-shrink-0">
                          <div><span className="text-muted-foreground text-xs">units</span><br/><span className="font-semibold">{line.quantity}</span></div>
                          {line.cases && <div><span className="text-muted-foreground text-xs">cases</span><br/><span>{line.cases}</span></div>}
                          {line.estimated_weight && <div><span className="text-muted-foreground text-xs">kg</span><br/><span>{parseFloat(line.estimated_weight).toFixed(2)}</span></div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Totals */}
                <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5" data-testid="review-totals">
                  <div className="grid grid-cols-3 gap-6 text-center">
                    <div>
                      <Hash className="w-5 h-5 mx-auto text-primary/60" />
                      <p className="text-2xl font-bold font-mono mt-1">{totals.units}</p>
                      <p className="text-xs text-muted-foreground">Total Units</p>
                    </div>
                    <div>
                      <Boxes className="w-5 h-5 mx-auto text-primary/60" />
                      <p className="text-2xl font-bold font-mono mt-1">{totals.cases}</p>
                      <p className="text-xs text-muted-foreground">Total Cases</p>
                    </div>
                    <div>
                      <Scale className="w-5 h-5 mx-auto text-primary/60" />
                      <p className="text-2xl font-bold font-mono mt-1">{totals.weight.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">Weight (kg)</p>
                    </div>
                  </div>
                </div>

                {notes && (
                  <div className="bg-card border rounded-2xl p-5 text-sm">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Notes</p>
                    <p>{notes}</p>
                  </div>
                )}
              </div>
              
              {/* Submit */}
              <div className="flex justify-center gap-3 pt-4">
                <Button variant="outline" size="lg" onClick={() => setStep(2)} className="h-14 px-8 text-base rounded-xl" data-testid="pos-btn-back-to-products">
                  Back to Products
                </Button>
                <Button size="lg" onClick={handleSave} disabled={saving} className="h-14 px-10 text-base rounded-xl shadow-lg bg-emerald-600 hover:bg-emerald-700" data-testid="pos-btn-submit-order">
                  {saving ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><Save className="w-5 h-5 mr-2" /> Submit Order</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Totals Bar (visible during step 2) */}
      {step === 2 && lines.length > 0 && (
        <div className="sticky bottom-0 z-20 bg-card/95 backdrop-blur-sm border-t px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]" data-testid="floating-totals">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2"><Hash className="w-4 h-4 text-muted-foreground" /><span className="font-mono font-bold text-lg">{totals.units}</span><span className="text-muted-foreground text-xs">units</span></div>
              <div className="flex items-center gap-2"><Boxes className="w-4 h-4 text-muted-foreground" /><span className="font-mono font-bold text-lg">{totals.cases}</span><span className="text-muted-foreground text-xs">cases</span></div>
              <div className="flex items-center gap-2"><Scale className="w-4 h-4 text-muted-foreground" /><span className="font-mono font-bold text-lg">{totals.weight.toFixed(1)}</span><span className="text-muted-foreground text-xs">kg</span></div>
            </div>
            <Button onClick={() => setStep(3)} className="h-11 px-6 rounded-xl" data-testid="pos-btn-floating-review">
              Review Order <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      <Dialog open={successDialog.open} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm text-center" data-testid="success-dialog">
          <div className="py-6">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <DialogTitle className="text-xl">Order Submitted!</DialogTitle>
            <DialogDescription className="mt-2">
              Order <span className="font-mono font-bold">{successDialog.orderNumber}</span> has been created successfully.
            </DialogDescription>
          </div>
          <div className="flex flex-col gap-2">
            <Button onClick={handleNewOrder} className="h-12 rounded-xl" data-testid="pos-btn-new-order">
              <Plus className="w-4 h-4 mr-2" /> New Order
            </Button>
            <Button variant="outline" onClick={() => navigate('/planner')} className="h-12 rounded-xl" data-testid="pos-btn-go-pipeline">
              Go to Order Pipeline
            </Button>
            <Button variant="ghost" onClick={() => navigate('/')} className="h-10" data-testid="pos-btn-go-dashboard">
              Back to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Customer Dialog */}
      <Dialog open={newCustomerDialogOpen} onOpenChange={setNewCustomerDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New Customer</DialogTitle></DialogHeader>
          <div className="py-4 space-y-3">
            <Label>Customer Name *</Label>
            <Input value={newCustomerName} onChange={e => setNewCustomerName(e.target.value)} placeholder="Enter customer name" className="h-12 text-base" data-testid="pos-input-new-customer"
              onKeyDown={e => e.key === 'Enter' && handleCreateCustomer()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCustomerDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCustomer} disabled={creatingCustomer} data-testid="pos-btn-create-customer">
              {creatingCustomer ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
